import React, {useContext, useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
  InputLabel, MenuItem, Select, Stack, Switch, TextField
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, CallIcon, Delete02Icon, FloppyDiskIcon, LanguageCircleIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useCatalog} from "../../../context/CatalogContext";
import {Context as AppContext} from "../../../context/AppContext";
import {ProjectEntity} from "../../../model/ModelData";
import useConfirm from "../../../hooks/useConfirm";
import {SubProjectRepository} from "../../../repositories/ProjectRepository";
import {filterByOrganization} from "../../../utils/organizationAccess";
import {subProjectLabel} from "../../../utils/subProjects";
import {
  LanguageConfigurations, SubHeader, toApiConfiguration, toVoiceGemini
} from "../../project-creation/components/LanguageConfigurations";
import {fieldSx, SectionHeader, selectLabelSx} from "../../project-creation/components/ProjectForm";
import {useRegistryProject} from "../RegistryProjectContext";
import {getFlow, mirrorToFlows, updateFlow} from "../flowSync";
import {SettingsFooter} from "./SettingsFooter";

const STATUSES = [
  {value: "BUILDING_SCENARIOS", label: "Building Scenarios"},
  {value: "IN_PROGRESS", label: "In Progress"},
  {value: "TESTING", label: "Testing"},
  {value: "PRODUCTION", label: "Production"},
];

// Core configuration keys of the API (snake_case, all values are strings)
const CORE_KEYS = [
  "max_calls_by_user", "chatbot_timeout", "redirect_vdn", "outbound_call", "success_tag", "redirect_message"
];

const DESCRIPTION_MAX = 4000;

// Form state of one sub project; voiceGemini is the voice stored in its Firebase flow
const toSubProjectConfig = (subProject, voiceGemini) => {
  const voice = subProject.voiceConfiguration || {};
  const transcribe = subProject.transcribeConfiguration || {};
  const core = subProject.coreConfiguration || {};
  return {
    name: subProject.name || "",
    description: subProject.description || "",
    status: subProject.status || "BUILDING_SCENARIOS",
    live: !!subProject.live,
    voiceConfiguration: {
      voiceModelId: voice.voiceModelId ?? null,
      voice: voice.voice || "",
      languageCode: voice.languageCode || subProject.languageCode || "",
      audioEncoding: voice.audioEncoding || "LINEAR16",
      pitch: voice.pitch ?? 0,
      speakingRate: voice.speakingRate ?? 1,
    },
    transcribeConfiguration: {
      transcribeModelId: transcribe.transcribeModelId ?? null,
      languageCode: transcribe.languageCode || subProject.languageCode || "",
    },
    coreConfiguration: Object.fromEntries(CORE_KEYS.map(key => [key, core[key] ?? ""])),
    // the style prompt is stored only in the Firebase flow
    prompt: voiceGemini?.input?.prompt || "",
  };
};

// The flow runtime reads the core configuration from the flow's `configuration`
const toFlowConfiguration = (core) => ({
  "configuration.max_calls_by_user": core.max_calls_by_user ? +core.max_calls_by_user : null,
  "configuration.chatbot_timeout": core.chatbot_timeout,
  "configuration.redirect_vdn": core.redirect_vdn,
  "configuration.outbound_call": core.outbound_call,
  "configuration.success_tag": core.success_tag || null,
  "configuration.redirect_message": core.redirect_message,
});

// models already used by the project stay selectable even when they are not in the user's own list
const withSelected = (models, selected) => [
  ...models,
  ...selected.filter(item => item.id && !models.some(model => model.id === item.id)),
];

// Flow settings a new sub project takes over from the project's existing flow; its own language and voice are set apart
const INHERITED_FLOW_SETTINGS = ["organization", "organization_type", "speechToText", "projectType", "mode", "category"];

// Sub projects tab: name, description, status, availability, voice, transcribe and core configuration of every
// sub project of the project, plus adding and deleting sub projects. Everything is keyed by sub project id, since
// several sub projects can share a language.
export const RegistrySubProjectSettings = () => {
  const {project, subProject: currentSubProject, refreshProject} = useRegistryProject();
  const {createProject} = useContext(AppContext);
  const {items: languages} = useCatalog("languages");
  const {items: myVoiceModels} = useCatalog("myVoiceModels", {refreshOnMount: true});
  const {items: myTranscribeModels} = useCatalog("myTranscribeModels", {refreshOnMount: true});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirmDelete = useConfirm();
  // {[subProjectId]: {voiceGemini, tags} of the sub project's flow}, null until loaded
  const [flows, setFlows] = useState(null);
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // ?add=1 (from the sub project switcher) opens the add dialog
  const addOpen = searchParams.get("add") === "1";
  const setAddOpen = (open) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (open) next.set("add", "1"); else next.delete("add");
    return next;
  }, {replace: true});

  // the selected sub project first
  const subProjects = useMemo(() => [...project.subProjects].sort((a, b) =>
    (b.id === currentSubProject?.id) - (a.id === currentSubProject?.id)
  ), [project.subProjects, currentSubProject?.id]);

  // keyed by the flow ids so a project refresh after saving does not reload the flows
  const flowUids = project.subProjects.map(item => `${item.id}:${item.flowUid}`).join(",");
  useEffect(() => {
    let cancelled = false;
    setFlows(null);
    Promise.all(project.subProjects.map(async item => {
      const settings = item.flowUid ? (await getFlow(item))?.settings : null;
      return [item.id, {voiceGemini: settings?.voiceGemini || null, tags: settings?.tags || []}];
    }))
      .then(entries => !cancelled && setFlows(Object.fromEntries(entries)))
      .catch(e => {
        console.error(e);
        if (!cancelled) toast.error("Failed to load the sub project flows");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowUids]);

  const savedConfigs = useMemo(() => flows && Object.fromEntries(project.subProjects.map(item => [
    item.id,
    toSubProjectConfig(item, flows[item.id]?.voiceGemini),
  ])), [project.subProjects, flows]);

  useEffect(() => {
    if (savedConfigs) setConfigs(savedConfigs);
  }, [savedConfigs]);

  // only public models and those of the project's organization can be used
  const voiceModels = withSelected(
    filterByOrganization(myVoiceModels, project.organizationId),
    project.subProjects.map(item => ({
      id: item.voiceConfiguration?.voiceModelId,
      name: item.voiceConfiguration?.voiceModelName,
    }))
  );
  const transcribeModels = withSelected(
    filterByOrganization(myTranscribeModels, project.organizationId),
    project.subProjects.map(item => ({
      id: item.transcribeConfiguration?.transcribeModelId,
      name: item.transcribeConfiguration?.transcribeModelName,
      languageId: item.languageId,
    }))
  );

  const isChanged = (item) => JSON.stringify(configs[item.id]) !== JSON.stringify(savedConfigs?.[item.id]);
  const changed = savedConfigs ? project.subProjects.filter(item => configs[item.id] && isChanged(item)) : [];

  const setConfig = (id, config) => setConfigs(prev => ({...prev, [id]: config}));

  const toFlowVoice = (item) => {
    const config = configs[item.id];
    const voiceGemini = toVoiceGemini(config, voiceModels);
    // a model outside the user's own list has no known model id, keep the one the flow already has
    if (voiceGemini && !voiceGemini.voice.modelName
      && config.voiceConfiguration.voiceModelId === item.voiceConfiguration?.voiceModelId) {
      voiceGemini.voice.modelName = flows[item.id]?.voiceGemini?.voice?.modelName || null;
    }
    return voiceGemini;
  };

  // names are unique within the project (case-insensitive); a blank name keeps the current one
  const nameError = (item) => {
    const name = (configs[item.id]?.name || "").trim().toLowerCase();
    if (!name) return null;
    return project.subProjects.some(other => other.id !== item.id
      && ((configs[other.id]?.name ?? other.name) || "").trim().toLowerCase() === name)
      ? "Another sub project already has this name"
      : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (changed.some(nameError)) {
      toast.error("Sub project names must be unique");
      return;
    }
    setSaving(true);
    try {
      for (const item of changed) {
        const {name, description, status, live, coreConfiguration} = configs[item.id];
        await SubProjectRepository.edit(item.id, {
          ...toApiConfiguration(configs[item.id]),
          // null keeps the current value: a cleared name keeps the old one, a cleared description is saved empty
          name: name.trim() || null,
          description: description.trim(),
          status, live, coreConfiguration,
        });
      }
      const voices = Object.fromEntries(changed.map(item => [item.id, toFlowVoice(item)]));
      await mirrorToFlows(changed, item => updateFlow(item, {
        "settings.voiceGemini": voices[item.id],
        status: configs[item.id].status,
        "settings.status": configs[item.id].status,
        "configuration.is_live": configs[item.id].live,
        ...toFlowConfiguration(configs[item.id].coreConfiguration),
      }));
      await refreshProject();
      setFlows(prev => ({
        ...prev,
        ...Object.fromEntries(changed.map(item => [item.id, {...prev[item.id], voiceGemini: voices[item.id]}])),
      }));
      toast.success("Sub projects updated");
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to update the sub projects");
    } finally {
      setSaving(false);
    }
  };

  // Creates the sub project, then its Firebase flow (the designer and the flow runtime read it). The flow takes the
  // organization, members and type of the project's existing flow.
  const handleAdd = async ({name, description, languageId}) => {
    let created = null;
    try {
      const response = await SubProjectRepository.create({
        projectId: project.id,
        languageId,
        name: name || null,
        description: description || null,
      });
      created = response.data;
      if (created?.flowUid) {
        const template = await getFlow(project.subProjects.find(item => item.flowUid)).catch(() => null);
        const language = languages.find(item => item.id === languageId);
        const flow = {
          ...JSON.parse(JSON.stringify(ProjectEntity)),
          id: created.flowUid,
          name: `${project.name} (${subProjectLabel(created)})`,
          description: created.description || project.description || "",
          is_demo: false,
          createdAt: new Date().getTime(),
          status: created.status || "BUILDING_SCENARIOS",
        };
        INHERITED_FLOW_SETTINGS.forEach(key => {
          if (template?.settings?.[key] !== undefined) flow.settings[key] = template.settings[key];
        });
        flow.settings.organization = project.organizationId || flow.settings.organization || null;
        flow.settings.language = languageId;
        flow.settings.lang = language?.lang || null;
        flow.settings.voiceGemini = null;
        if (template?.members) flow.members = template.members;
        await createProject(flow);
      }
      await refreshProject();
      toast.success(`Added "${subProjectLabel(created)}"`);
      return true;
    } catch (error) {
      console.error(error);
      if (created) {
        // the sub project exists; only its flow failed, the designer reports a missing flow
        await refreshProject();
        toast.warn("The sub project was added, but its flow could not be created");
        return true;
      }
      toast.error(error?.response?.data?.message || "Failed to add the sub project");
      return false;
    }
  };

  const handleDelete = async (item) => {
    const confirmed = await confirmDelete({
      title: `Delete "${subProjectLabel(item)}"?`,
      message: "Its configuration and versions are deleted. This cannot be undone.",
      confirmText: "Delete",
    });
    if (!confirmed) return;
    setDeletingId(item.id);
    try {
      await SubProjectRepository.delete(item.id);
      // the page of a deleted sub project no longer exists, move to another one first
      if (item.id === currentSubProject?.id) {
        const next = project.subProjects.find(other => other.id !== item.id);
        navigate(`/registry-project/${project.id}/${next.id}/settings?tab=sub-projects`, {replace: true});
      }
      await refreshProject();
      toast.success(`Deleted "${subProjectLabel(item)}"`);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to delete the sub project");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
    <AddSubProjectDialog
      open={addOpen}
      handleClose={() => setAddOpen(false)}
      languages={languages || []}
      subProjects={project.subProjects}
      onSubmit={handleAdd}
    />
    <Box component={"form"} onSubmit={handleSubmit}>
      <Box sx={{px: {xs: 2, md: 4}, py: {xs: 2, md: 3}}}>
        <Stack direction={{xs: "column", sm: "row"}} justifyContent={"space-between"} alignItems={{sm: "flex-start"}} gap={1}>
          <SectionHeader
            icon={<HugeiconsIcon icon={LanguageCircleIcon} size={18}/>}
            title={"Sub Projects"}
            description={"Each sub project has its own language, flow, voice and call handling."}
          />
          <Button
            type={"button"} variant={"primary"} onClick={() => setAddOpen(true)} sx={{flexShrink: 0}}
            startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
          >
            Add Sub Project
          </Button>
        </Stack>
        {!savedConfigs ? (
          <Stack alignItems={"center"} sx={{py: 4}}><CircularProgress size={28}/></Stack>
        ) : (
          <LanguageConfigurations
            languageIds={subProjects.map(item => item.id)}
            languages={subProjects.map(item => ({
              id: item.id, languageId: item.languageId, language: subProjectLabel(item), code: item.languageCode,
            }))}
            configs={configs}
            voiceModels={voiceModels}
            transcribeModels={transcribeModels}
            fieldSx={fieldSx}
            selectLabelSx={selectLabelSx}
            onChange={setConfig}
            renderExtra={(id, config) => {
              const item = project.subProjects.find(other => other.id === id);
              const error = item && nameError(item);
              return (
                <Grid container spacing={2} alignItems={"center"}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Name"} fullWidth value={config.name} sx={fieldSx} InputLabelProps={{shrink: true}}
                      onChange={e => setConfig(id, {...config, name: e.target.value})}
                      placeholder={item?.languageName} inputProps={{maxLength: 255}}
                      error={Boolean(error)} helperText={error || " "}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={"Language"} fullWidth disabled value={item?.languageName || item?.languageCode || ""}
                      sx={fieldSx} InputLabelProps={{shrink: true}} helperText={"The language cannot be changed"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label={"Description"} fullWidth multiline minRows={2} value={config.description} sx={fieldSx}
                      InputLabelProps={{shrink: true}} inputProps={{maxLength: DESCRIPTION_MAX}}
                      onChange={e => setConfig(id, {...config, description: e.target.value})}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id={`status-${id}`} shrink sx={selectLabelSx}>Status</InputLabel>
                      <Select
                        labelId={`status-${id}`}
                        label={"Status"}
                        value={config.status}
                        sx={fieldSx}
                        onChange={e => setConfig(id, {...config, status: e.target.value})}
                      >
                        {STATUSES.map(status => <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}>
                      <FormControlLabel
                        label={config.live ? "Live" : "Not live"}
                        control={
                          <Switch
                            checked={config.live}
                            color={"secondary"}
                            onChange={(e, live) => setConfig(id, {...config, live})}
                          />
                        }
                      />
                      <Button
                        type={"button"} variant={"ghost"} color={"error"}
                        disabled={project.subProjects.length <= 1 || deletingId != null}
                        title={project.subProjects.length <= 1 ? "A project needs at least one sub project" : undefined}
                        onClick={() => item && handleDelete(item)}
                        startIcon={deletingId === id
                          ? <CircularProgress size={14} color={"inherit"}/>
                          : <HugeiconsIcon icon={Delete02Icon} size={16}/>}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              );
            }}
            renderAfter={(id, config) => (
              <CoreConfigurationFields
                subProjectId={id}
                core={config.coreConfiguration}
                tags={flows[id]?.tags || []}
                onChange={(key, value) => setConfig(id, {
                  ...config, coreConfiguration: {...config.coreConfiguration, [key]: value},
                })}
              />
            )}
          />
        )}
      </Box>

      <SettingsFooter>
        <Button
          type={"button"} variant={"ghost"} color={"secondary"} disabled={!changed.length || saving}
          onClick={() => setConfigs(savedConfigs)}
        >
          Reset
        </Button>
        <Button
          type={"submit"} variant={"primary"} disabled={!changed.length || saving}
          startIcon={<HugeiconsIcon icon={FloppyDiskIcon} size={18}/>}
        >
          Save Changes
        </Button>
      </SettingsFooter>
    </Box>
    </>
  );
};

// New sub project: a required language, plus a name (defaults to the language's name) and a description
const AddSubProjectDialog = ({open, handleClose, languages, subProjects, onSubmit}) => {
  const [languageId, setLanguageId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLanguageId("");
    setName("");
    setDescription("");
  }, [open]);

  const languageName = languages.find(item => item.id === languageId)?.language || "";
  const effectiveName = name.trim() || languageName;
  const duplicate = Boolean(effectiveName) && subProjects.some(item =>
    (item.name || "").trim().toLowerCase() === effectiveName.toLowerCase());

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!languageId || duplicate) return;
    setLoading(true);
    const success = await onSubmit({languageId, name: name.trim(), description: description.trim()});
    setLoading(false);
    if (success) handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
        sx: {minWidth: "360px", maxWidth: "sm", borderRadius: 4, p: 2, width: "100%"},
      }}
    >
      <DialogTitle>Add Sub Project</DialogTitle>
      <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, py: "16px !important"}}>
        <TextField
          select label={"Language"} required fullWidth value={languageId}
          onChange={e => setLanguageId(e.target.value)}
          helperText={"Cannot be changed later"}
        >
          {languages.map(item => <MenuItem key={item.id} value={item.id}>{item.language} ({item.code})</MenuItem>)}
        </TextField>
        <TextField
          label={"Name"} fullWidth value={name} onChange={e => setName(e.target.value)}
          placeholder={languageName || "Defaults to the language name"} InputLabelProps={{shrink: true}}
          inputProps={{maxLength: 255}}
          error={duplicate}
          helperText={duplicate
            ? `"${effectiveName}" is already used in this project${name.trim() ? "" : ", enter a name"}`
            : "Must be unique within the project"}
        />
        <TextField
          label={"Description"} fullWidth multiline minRows={2} value={description}
          onChange={e => setDescription(e.target.value)} inputProps={{maxLength: DESCRIPTION_MAX}}
        />
      </DialogContent>
      <DialogActions sx={{px: 2, py: 1}}>
        <Button variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button type={"submit"} variant={"modal"} disabled={loading || !languageId || duplicate}>
          {loading ? <CircularProgress size={16} color={"inherit"}/> : "Add Sub Project"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Call handling of one sub project: limits, timeout and where calls are redirected.
// tags are the tags of the sub project's flow, used for the success tag.
const CoreConfigurationFields = ({subProjectId, core, tags, onChange}) => {
  const field = (key) => ({
    value: core[key],
    onChange: e => onChange(key, e.target.value),
    fullWidth: true,
    sx: fieldSx,
    InputLabelProps: {shrink: true},
  });
  // a tag that no longer exists in the flow stays selectable
  const tagOptions = !core.success_tag || tags.some(tag => String(tag?.id) === core.success_tag)
    ? tags
    : [...tags, {id: core.success_tag, name: core.success_tag}];

  return (
    <>
      <SubHeader icon={CallIcon} title={"Core Configuration"}/>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("max_calls_by_user")}
            label={"Maximum Call Attempts Per User"}
            type={"number"}
            placeholder={"3"}
            inputProps={{min: 0}}
            helperText={"Calls a user can make within 6 hours before being redirected"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("chatbot_timeout")}
            label={"Chatbot Timeout"}
            type={"number"}
            placeholder={"27"}
            inputProps={{min: 0}}
            helperText={"Seconds to wait for a response before redirecting"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("redirect_vdn")}
            label={"Redirect VDN Number"}
            helperText={"Transfer destination when the chatbot does not respond"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            {...field("outbound_call")}
            label={"Outbound Caller ID"}
            helperText={"Falls back to the Redirect VDN Number when left empty"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id={`success-tag-${subProjectId}`} shrink sx={selectLabelSx}>Success Tag</InputLabel>
            <Select
              labelId={`success-tag-${subProjectId}`}
              label={"Success Tag"}
              value={core.success_tag}
              displayEmpty
              sx={fieldSx}
              onChange={e => onChange("success_tag", e.target.value)}
            >
              <MenuItem value={""}><em>None</em></MenuItem>
              {tagOptions.map(tag => (
                <MenuItem key={tag?.id} value={String(tag?.id)}>{tag?.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            {...field("redirect_message")}
            label={"Redirect Message"}
            placeholder={"We're sorry, but the chatbot is currently unavailable to respond. Please hold while we redirect your request."}
            multiline
            rows={3}
          />
        </Grid>
      </Grid>
    </>
  );
};
